import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import minMax from 'dayjs/plugin/minMax'
import utc from 'dayjs/plugin/utc'

import { isNil } from 'lodash'
import {
  ALL_NUMERIC_COMPARISON_OPERATORS,
  CONCAT_MAX_SOURCES,
  CONCAT_MIN_SOURCES,
  ConditionType,
  NumericComparisonOperator,
  NormalizeType,
  type ConcatRule,
  type MappingRule,
  type NormalizeStep,
  type SourceEntry,
} from '../types'

dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(minMax)

type RuleTransformer = (sourceRow: Record<string, string>) => string

/** Result of evaluating a rule that can block a record (currently `concat`). */
interface RuleEvaluation {
  value: string
  error: string | null
}

interface BuiltRule {
  target: string
  apply: RuleTransformer
  /**
   * Present only for rules that can surface a per-record error (e.g. `concat`).
   * When set, `transformRows` uses it to collect errors into the `Errors`
   * column instead of just reading `apply`.
   */
  evaluate?: (sourceRow: Record<string, string>) => RuleEvaluation
}

/**
 * Strict formats used to parse get_max_date source values. HRIS files mix ISO
 * (YYYY-MM-DD) date columns with US (M/D/YYYY) columns, so both are parsed
 * deterministically to select the true maximum before emitting zero-padded
 * MM/DD/YYYY. Keep in sync with the backend MappingRuleEngine.
 */
const GET_MAX_DATE_INPUT_FORMATS = [
  'YYYY-MM-DD',
  'YYYY/MM/DD',
  'M/D/YYYY',
  'M/DD/YYYY',
  'MM/D/YYYY',
  'MM/DD/YYYY',
  'M-D-YYYY',
  'M-DD-YYYY',
  'MM-D-YYYY',
  'MM-DD-YYYY',
]

function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function parseWithFormats(
  date: string | Date,
  formats?: string[]
): dayjs.Dayjs {
  if (formats && formats.length > 0 && typeof date === 'string') {
    const strict = dayjs(date, formats, true)
    return strict.isValid() ? strict : dayjs(date)
  }
  return dayjs(date)
}

export function parseNumericInput(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const normalized = trimmed.replace(/[$,\s]/g, '')
  if (!normalized) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

function compareNumericValues(
  sourceValue: number,
  operator: NumericComparisonOperator,
  compareValue: number
): boolean {
  switch (operator) {
    case NumericComparisonOperator.EQUAL:
      return sourceValue === compareValue
    case NumericComparisonOperator.LESS_THAN:
      return sourceValue < compareValue
    case NumericComparisonOperator.LESS_THAN_OR_EQUAL:
      return sourceValue <= compareValue
    case NumericComparisonOperator.MORE_THAN:
      return sourceValue > compareValue
    case NumericComparisonOperator.MORE_THAN_OR_EQUAL:
      return sourceValue >= compareValue
  }
}

const NUMERIC_COMPARISON_OPERATOR_SET = new Set<string>(
  ALL_NUMERIC_COMPARISON_OPERATORS
)

function isNumericComparisonOperator(
  operator: unknown
): operator is NumericComparisonOperator {
  return (
    typeof operator === 'string' &&
    NUMERIC_COMPARISON_OPERATOR_SET.has(operator)
  )
}

export function getMaxDayjs(
  dates: (string | Date | undefined | null)[],
  formats?: string[]
): dayjs.Dayjs | null {
  const validDayjs = dates
    .filter(
      (date): date is string | Date =>
        date !== null && date !== undefined && date !== ''
    )
    .map((date) => parseWithFormats(date, formats))
    .filter((parsed) => parsed.isValid())
  const maxDate = dayjs?.max?.(validDayjs)
  return maxDate ?? null
}

function buildNormalizeFunction(
  step: NormalizeStep
): (value: string) => string {
  switch (step.type) {
    case NormalizeType.TRIM:
      return (v) => v.trim()
    case NormalizeType.UPPERCASE:
      return (v) => v.toUpperCase()
    case NormalizeType.LOWERCASE:
      return (v) => v.toLowerCase()
    case NormalizeType.REMOVE_SPECIAL_CHARS: {
      const chars = new Set(step.chars)
      return (v) =>
        Array.from(v)
          .filter((c) => !chars.has(c))
          .join('')
    }
    case NormalizeType.SUBSTRING:
      return (v) => v.substring(step.start, step.start + step.length)
    case NormalizeType.PAD_LEFT:
      return (v) => v.padStart(step.length, step.pad_char)
    case NormalizeType.PAD_RIGHT:
      return (v) => v.padEnd(step.length, step.pad_char)
    case NormalizeType.DATE_FORMAT:
      return (v) => {
        const parsed = dayjs(v)
        return parsed.isValid() ? parsed.format(step.format) : v
      }
    case NormalizeType.MAX_LENGTH:
      return (v) => v.substring(0, step.length)
    case NormalizeType.REPLACE: {
      const flags = step.case_insensitive ? 'gi' : 'g'
      const regex = new RegExp(escapeRegExp(step.pattern), flags)
      return (v) => v.replace(regex, step.replacement)
    }
    case NormalizeType.SLICE_LEFT:
      return (v) => v.slice(0, step.length)
    case NormalizeType.SLICE_RIGHT:
      return (v) => v.slice(-step.length)
  }
}

function buildNormalizeChain(
  steps: NormalizeStep[]
): (value: string) => string {
  if (steps.length === 0) return (value: string) => value
  const fns = steps.map((step) => buildNormalizeFunction(step))
  return (value: string) => fns.reduce((acc, fn) => fn(acc), value)
}

function resolveNestedMapping(
  mapping: unknown,
  values: string[]
): string | undefined {
  if (values.length === 0 || mapping === undefined || mapping === null) {
    return typeof mapping === 'string' ? mapping : undefined
  }

  if (typeof mapping !== 'object') {
    return undefined
  }

  const [current, ...remaining] = values
  const level = mapping as Record<string, unknown>
  const exactMatch = level[current]
  const wildcardMatch = level['*']

  return (
    resolveNestedMapping(exactMatch, remaining) ??
    resolveNestedMapping(wildcardMatch, remaining)
  )
}

function buildRuleTransformer(rule: MappingRule): BuiltRule {
  switch (rule.condition) {
    case ConditionType.HARDCODE:
      return buildHardcode(rule)
    case ConditionType.LOOKUP_MAP:
      return buildLookupMap(rule)
    case ConditionType.GET_MAX_DATE:
      return buildGetMaxDate(rule)
    case ConditionType.DIRECT_MAPPING:
      return buildDirectMapping(rule)
    case ConditionType.FIRST_AVAILABLE:
      return buildFirstAvailable(rule)
    case ConditionType.FIRST_AVAILABLE_MAP:
      return buildFirstAvailableMap(rule)
    case ConditionType.CONTAINS_MAP:
      return buildContainsMap(rule)
    case ConditionType.MULTIPLY:
      return buildMultiply(rule)
    case ConditionType.CONDITIONAL_NUMERIC:
      return buildConditionalNumeric(rule)
    case ConditionType.CONCAT:
      return buildConcat(rule)
    default:
      throw new Error(`Unsupported condition type: ${rule.condition}`)
  }
}

function buildHardcode(rule: MappingRule): {
  target: string
  apply: RuleTransformer
} {
  return {
    target: rule.target,
    apply: () => rule.value ?? '',
  }
}

function buildLookupMap(rule: MappingRule): {
  target: string
  apply: RuleTransformer
} {
  const isSingleSource = rule.source !== undefined

  if (isSingleSource) {
    return {
      target: rule.target,
      apply: (sourceRow) => {
        const value = sourceRow[rule.source?.trim() as string]
        if (value === undefined || value === null) {
          return rule.default ?? ''
        }
        const mapped = (rule.mapping as Record<string, string>)?.[value.trim()]
        return mapped ?? rule.default ?? ''
      },
    }
  }

  return {
    target: rule.target,
    apply: (sourceRow) => {
      const sources = rule.sources ?? []
      if (sources.length === 0) {
        return rule.default ?? ''
      }
      const values = sources.map((src) => sourceRow[src.trim()]?.trim() ?? '')
      const result = resolveNestedMapping(rule.mapping, values)
      return result ?? rule.default ?? ''
    },
  }
}

function buildGetMaxDate(rule: MappingRule): {
  target: string
  apply: RuleTransformer
} {
  return {
    target: rule.target,
    apply: (sourceRow) => {
      const dateValues = (rule.sources ?? []).map((src) =>
        sourceRow[src.trim()]?.trim()
      )
      const maxDate = getMaxDayjs(dateValues, GET_MAX_DATE_INPUT_FORMATS)
      if (!maxDate) return ''
      return maxDate.format('MM/DD/YYYY')
    },
  }
}

function buildDirectMapping(rule: MappingRule): {
  target: string
  apply: RuleTransformer
} {
  const normalizeChain = buildNormalizeChain(rule.normalize ?? [])
  return {
    target: rule.target,
    apply: (sourceRow) => {
      const value = sourceRow[rule.source?.trim() ?? '']
      if (value === undefined || value === null) return ''
      return normalizeChain(value)
    },
  }
}

function buildFirstAvailable(rule: MappingRule): {
  target: string
  apply: RuleTransformer
} {
  const normalizeChain = buildNormalizeChain(rule.normalize ?? [])
  return {
    target: rule.target,
    apply: (sourceRow) => {
      for (const source of rule.sources ?? []) {
        const value = sourceRow[source.trim()]
        if (value !== undefined && value !== null && value !== '') {
          return normalizeChain(value)
        }
      }
      return ''
    },
  }
}

function buildFirstAvailableMap(rule: MappingRule): {
  target: string
  apply: RuleTransformer
} {
  return {
    target: rule.target,
    apply: (sourceRow) => {
      for (const entry of (rule.sourceEntries ?? []) as SourceEntry[]) {
        const value = sourceRow[entry.source.trim()]
        if (value !== undefined && value !== null && value !== '') {
          return entry.value
        }
      }
      return rule.default ?? ''
    },
  }
}

function buildMultiply(rule: MappingRule): {
  target: string
  apply: RuleTransformer
} {
  const source = (rule.source ?? '').trim()
  const constant = rule.constant
  // Treat a missing or non-finite constant as a misconfigured rule rather than
  // silently multiplying by 0. We surface this as an empty target value,
  // matching the "empty / non-numeric source resolves to ''" semantics so the
  // user can see (and fix) the issue instead of destroying data.
  const hasValidConstant =
    typeof constant === 'number' && Number.isFinite(constant)
  return {
    target: rule.target,
    apply: (sourceRow) => {
      if (!hasValidConstant) return ''
      const raw = sourceRow[source]
      if (raw === undefined || raw === null || raw === '') return ''
      const num = Number(raw)
      if (Number.isNaN(num)) return ''
      const rounded = Math.round(num * constant * 100) / 100
      return String(rounded)
    },
  }
}

function buildContainsMap(rule: MappingRule): {
  target: string
  apply: RuleTransformer
} {
  const caseInsensitive = rule.case_insensitive !== false
  const mapping = (rule.mapping ?? {}) as Record<string, string>
  const source = rule.source ?? ''
  const entries = Object.entries(mapping).map(([pattern, mapped]) => ({
    pattern: caseInsensitive ? pattern.toLowerCase() : pattern,
    mapped,
  }))
  return {
    target: rule.target,
    apply: (sourceRow) => {
      const raw = sourceRow[source.trim()]
      if (isNil(raw)) return rule.default ?? ''
      const value = caseInsensitive ? raw.toLowerCase() : raw
      for (const { pattern, mapped } of entries) {
        if (value.includes(pattern)) return mapped
      }
      return rule.default ?? ''
    },
  }
}

function buildConditionalNumeric(rule: MappingRule): {
  target: string
  apply: RuleTransformer
} {
  const sourceField = (rule.sourceField ?? '').trim()
  const rules = rule.rules ?? []
  return {
    target: rule.target,
    apply: (sourceRow) => {
      const sourceValue = parseNumericInput(sourceRow[sourceField])
      if (sourceValue === null) return ''

      for (const condition of rules) {
        const compareValue = parseNumericInput(condition.compareValue)
        if (compareValue === null) continue
        if (
          compareNumericValues(sourceValue, condition.operator, compareValue)
        ) {
          return condition.outputValue ?? ''
        }
      }

      return ''
    },
  }
}

/**
 * Runtime evaluation of a `concat` rule. Ported 1:1 from the backend
 * `MappingRuleEngine.evaluateConcat`. Each source value is trimmed before
 * joining; if any source is missing/empty/whitespace-only the whole record is
 * blocked (no partial output, no dangling delimiter) and the first empty field
 * is reported. Keep the error string identical to the backend.
 */
export function evaluateConcat(
  rule: ConcatRule,
  sourceRow: Record<string, string>
): RuleEvaluation {
  const values: string[] = []
  for (const source of rule.sources) {
    // Look up and report using the trimmed key so the error names the actual
    // CSV column (padded source keys are allowed by validation/getSourceColumns
    // but the row is keyed by the trimmed column name).
    const key = source.trim()
    const raw = sourceRow[key]
    const trimmed = raw?.trim() ?? ''
    if (trimmed === '') {
      return { value: '', error: `CONCAT failed: field "${key}" is empty` }
    }
    values.push(trimmed)
  }
  return { value: values.join(rule.delimiter), error: null }
}

/**
 * Config-time validation of a `concat` rule. Ported 1:1 from the backend
 * `validateConcatRule`. Returns `null` when valid, otherwise an error string
 * that must match the backend byte-for-byte (`prefix` is supplied by the
 * caller, e.g. `Rule[0] (target="CompanyEmployeeId")`).
 */
export function validateConcatRule(
  rule: ConcatRule,
  prefix: string
): string | null {
  if (!Array.isArray(rule.sources)) {
    return `${prefix}: "concat" condition requires a "sources" array`
  }
  if (rule.sources.length < CONCAT_MIN_SOURCES) {
    return `${prefix}: "concat" condition requires at least ${CONCAT_MIN_SOURCES} source fields`
  }
  if (rule.sources.length > CONCAT_MAX_SOURCES) {
    return `${prefix}: "concat" condition allows at most ${CONCAT_MAX_SOURCES} source fields, but ${rule.sources.length} were provided`
  }
  for (const src of rule.sources) {
    if (typeof src !== 'string' || src.trim() === '') {
      return `${prefix}: All items in "sources" must be non-empty strings`
    }
  }
  if (typeof rule.delimiter !== 'string') {
    return `${prefix}: "concat" condition requires a string "delimiter"`
  }
  return null
}

/**
 * Runtime view of a concat rule with safe defaults so `evaluateConcat` never
 * crashes on a malformed/imported rule (missing sources → [], missing
 * delimiter → ""). Config validation must NOT use this — it needs the raw
 * values to reproduce the backend errors, so it builds its own object.
 */
function toRuntimeConcatRule(rule: MappingRule): ConcatRule {
  return {
    target: rule.target,
    condition: ConditionType.CONCAT,
    sources: (rule.sources ?? []) as string[],
    delimiter: rule.delimiter ?? '',
  }
}

/**
 * Config-validation view of a concat rule. Keeps `sources`/`delimiter` exactly
 * as authored (possibly undefined/non-string for an imported config) so
 * `validateConcatRule` emits the same errors as the backend.
 */
function toConfigConcatRule(rule: MappingRule): ConcatRule {
  return {
    target: rule.target,
    condition: ConditionType.CONCAT,
    sources: rule.sources as unknown as string[],
    delimiter: rule.delimiter as unknown as string,
  }
}

function buildConcat(rule: MappingRule): BuiltRule {
  const concatRule = toRuntimeConcatRule(rule)
  const evaluate = (sourceRow: Record<string, string>) =>
    evaluateConcat(concatRule, sourceRow)
  return {
    target: rule.target,
    apply: (sourceRow) => evaluate(sourceRow).value,
    evaluate,
  }
}

/**
 * Write one built rule onto `destRow`. CONCAT (and any future `evaluate`
 * rule) records its error on `rowErrors` instead of silently using `apply`.
 */
function applyBuiltRule(
  sourceRow: Record<string, string>,
  destRow: Record<string, string>,
  { target, apply, evaluate }: BuiltRule,
  rowErrors: string[]
): void {
  if (evaluate) {
    const { value, error } = evaluate(sourceRow)
    destRow[target] = value?.trim?.() ?? value
    if (error) rowErrors.push(error)
    return
  }
  const value = apply(sourceRow)
  destRow[target] = value?.trim?.() ?? value
}

/**
 * Apply computed-field rules in order to each source row, matching the
 * working-row values used during transform (CONCAT evaluate, not apply).
 * Returns the input rows unchanged when there are no computed fields.
 * Runtime CONCAT errors are collected by `transformRows` for preview/export.
 */
export function applyComputedFieldsToRows(
  rows: Record<string, string>[],
  computedFieldRules: MappingRule[]
): Record<string, string>[] {
  if (computedFieldRules.length === 0) return rows
  const transformers = computedFieldRules.map((rule) =>
    buildRuleTransformer(rule)
  )
  return rows.map((row) => {
    const workingRow: Record<string, string> = { ...row }
    for (const transformer of transformers) {
      applyBuiltRule(workingRow, workingRow, transformer, [])
    }
    return workingRow
  })
}

export function transformRows(
  rules: MappingRule[],
  rows: Record<string, string>[],
  computedFieldRules: MappingRule[] = []
): Record<string, string>[] {
  const computedTransformers = computedFieldRules.map((rule) =>
    buildRuleTransformer(rule)
  )
  const ruleTransformers = rules.map((rule) => buildRuleTransformer(rule))
  return rows.map((row) => {
    const workingRow: Record<string, string> = { ...row }
    const rowErrors: string[] = []
    for (const transformer of computedTransformers) {
      applyBuiltRule(workingRow, workingRow, transformer, rowErrors)
    }
    const targetRow: Record<string, string> = {}
    for (const transformer of ruleTransformers) {
      applyBuiltRule(workingRow, targetRow, transformer, rowErrors)
    }
    if (rowErrors.length > 0) {
      targetRow.Errors = rowErrors.join('; ')
    }
    return targetRow
  })
}

function ruleToBackendShape(rule: MappingRule): Record<string, unknown> {
  const base: Record<string, unknown> = {
    target: rule.target,
    condition: rule.condition,
  }

  switch (rule.condition) {
    case ConditionType.HARDCODE:
      base.value = rule.value ?? ''
      break
    case ConditionType.DIRECT_MAPPING:
      base.source = rule.source ?? ''
      if (rule.normalize && rule.normalize.length > 0) {
        base.normalize = rule.normalize
      }
      break
    case ConditionType.GET_MAX_DATE:
      base.sources = rule.sources ?? []
      break
    case ConditionType.LOOKUP_MAP:
      if (rule.sources && rule.sources.length > 1) {
        base.sources = rule.sources
      } else {
        base.source = rule.source ?? ''
      }
      base.mapping = rule.mapping ?? {}
      if (rule.default !== undefined) {
        base.default = rule.default
      }
      break
    case ConditionType.FIRST_AVAILABLE:
      base.sources = rule.sources ?? []
      if (rule.normalize && rule.normalize.length > 0) {
        base.normalize = rule.normalize
      }
      break
    case ConditionType.FIRST_AVAILABLE_MAP:
      base.sources = rule.sourceEntries ?? []
      if (rule.default !== undefined) {
        base.default = rule.default
      }
      break
    case ConditionType.CONTAINS_MAP:
      base.source = rule.source ?? ''
      base.mapping = rule.mapping ?? {}
      if (rule.default !== undefined) {
        base.default = rule.default
      }
      if (rule.case_insensitive !== undefined) {
        base.case_insensitive = rule.case_insensitive
      }
      break
    case ConditionType.MULTIPLY:
      base.source = rule.source ?? ''
      // Only emit constant when it's a valid finite number. Falling back to
      // 0 here would silently encode a data-destroying multiplier into the
      // exported schema if the user cleared the input.
      if (typeof rule.constant === 'number' && Number.isFinite(rule.constant)) {
        base.constant = rule.constant
      }
      break
    case ConditionType.CONDITIONAL_NUMERIC:
      base.sourceField = rule.sourceField ?? ''
      base.rules = (rule.rules ?? []).map((condition) => {
        const compareValue = parseNumericInput(condition.compareValue)
        return {
          operator: condition.operator,
          compareValue: compareValue ?? condition.compareValue,
          outputValue: condition.outputValue ?? '',
        }
      })
      break
    case ConditionType.CONCAT:
      base.sources = rule.sources ?? []
      // Always emit delimiter (even ""), matching the backend contract
      // where an empty string means a plain, separator-less join.
      base.delimiter = rule.delimiter ?? ''
      break
  }

  return base
}

export function exportRulesJSON(
  rules: MappingRule[],
  computedFieldRules: MappingRule[] = []
): string {
  const schema = {
    schema_version: '1.0',
    ...(computedFieldRules.length > 0
      ? { computed_fields: computedFieldRules.map(ruleToBackendShape) }
      : {}),
    mapping_rules: rules.map(ruleToBackendShape),
  }

  return JSON.stringify(schema, null, 2)
}

function ruleFromBackendShape(
  rule: Record<string, unknown>,
  index: number,
  idPrefix: string
): MappingRule {
  const base: MappingRule = {
    id: `${idPrefix}-${index}-${Date.now()}`,
    target: (rule.target as string) ?? '',
    condition:
      (rule.condition as MappingRule['condition']) ??
      ConditionType.DIRECT_MAPPING,
    value: rule.value as string | undefined,
    source: rule.source as string | undefined,
    mapping: rule.mapping as Record<string, unknown> | undefined,
    default: rule.default as string | undefined,
    normalize: rule.normalize as MappingRule['normalize'],
    case_insensitive: rule.case_insensitive as boolean | undefined,
    constant: typeof rule.constant === 'number' ? rule.constant : undefined,
    sourceField: rule.sourceField as string | undefined,
    rules: rule.rules as MappingRule['rules'],
    // Kept as the raw value (may be non-string for a malformed import) so
    // validateConcatRule can flag it; UI-created rules are always strings.
    delimiter: rule.delimiter as string | undefined,
  }

  if (rule.condition === ConditionType.FIRST_AVAILABLE_MAP) {
    base.sourceEntries = rule.sources as SourceEntry[] | undefined
  } else {
    base.sources = rule.sources as string[] | undefined
  }

  return base
}

export interface ImportedMappingRules {
  mappingRules: MappingRule[]
  computedFields: MappingRule[]
}

export function importRulesJSON(json: string): ImportedMappingRules {
  const parsed = JSON.parse(json)
  const mappingRulesRaw: Record<string, unknown>[] = parsed.mapping_rules ?? []
  const computedFieldsRaw: Record<string, unknown>[] =
    parsed.computed_fields ?? []
  return {
    mappingRules: mappingRulesRaw.map((rule, index) =>
      ruleFromBackendShape(rule, index, 'imported')
    ),
    computedFields: computedFieldsRaw.map((rule, index) =>
      ruleFromBackendShape(rule, index, 'imported-computed')
    ),
  }
}

function validateConditionalNumericRow(
  rule: MappingRule,
  label: string
): string[] {
  if (rule.condition !== ConditionType.CONDITIONAL_NUMERIC) return []

  const errors: string[] = []
  if (!rule.sourceField?.trim()) {
    errors.push(`${label}: source field is required.`)
  }

  const conditions = rule.rules ?? []
  if (conditions.length === 0) {
    errors.push(`${label}: at least one comparison rule is required.`)
  }

  conditions.forEach((condition, conditionIndex) => {
    const rowLabel = `${label}, condition ${conditionIndex + 1}`
    if (!condition.operator) {
      errors.push(`${rowLabel}: operator is required.`)
    } else if (!isNumericComparisonOperator(condition.operator)) {
      errors.push(`${rowLabel}: operator is not supported.`)
    }
    if (parseNumericInput(condition.compareValue) === null) {
      errors.push(`${rowLabel}: compare value must be numeric.`)
    }
    if (!condition.outputValue?.trim()) {
      errors.push(`${rowLabel}: output value is required.`)
    }
  })

  return errors
}

function validateRuleShape(
  rule: MappingRule,
  concatPrefix: string,
  label: string
): string[] {
  if (rule.condition === ConditionType.CONCAT) {
    const concatError = validateConcatRule(
      toConfigConcatRule(rule),
      concatPrefix
    )
    return concatError ? [concatError] : []
  }
  return validateConditionalNumericRow(rule, label)
}

export function validateMappingRules(
  rules: MappingRule[],
  computedFieldRules: MappingRule[] = []
): string[] {
  const errors: string[] = []

  const computedTargets = new Set<string>()
  computedFieldRules.forEach((rule, index) => {
    const label = rule.target?.trim() || `Computed field ${index + 1}`
    if (rule.target && computedTargets.has(rule.target)) {
      errors.push(`Duplicate computed field target: "${rule.target}".`)
    }
    if (rule.target) computedTargets.add(rule.target)
    // Backend prefix is `Computed field[<index>] (target="<target>")`.
    const concatPrefix = `Computed field[${index}] (target="${rule.target ?? ''}")`
    errors.push(...validateRuleShape(rule, concatPrefix, label))
  })

  const seenTargets = new Set<string>()
  rules.forEach((rule, ruleIndex) => {
    // Config-level rule (ported from the backend): every target must be
    // non-empty and unique within the rule set. Emit the exact backend string
    // so operators see identical feedback front-end and back-end.
    const target = rule.target?.trim() ?? ''
    if (target !== '') {
      if (seenTargets.has(target)) {
        errors.push(
          `Duplicate target field: "${target}". Each target must be unique within a rule set`
        )
      } else {
        seenTargets.add(target)
      }
    }

    const label = rule.target?.trim() || `Rule ${ruleIndex + 1}`
    // Backend builds the prefix as `Rule[<index>] (target="<target>")` so
    // the concat error strings match byte-for-byte.
    const concatPrefix = `Rule[${ruleIndex}] (target="${rule.target ?? ''}")`
    errors.push(...validateRuleShape(rule, concatPrefix, label))
  })

  return errors
}
